import { createSlice } from '@reduxjs/toolkit';
import type {PayloadAction} from "@reduxjs/toolkit";
import { Languages, SaveStates, SimulationStates } from '../../globals';
import { IClipboard, IInspectionTarget, IPublishSettings, InspectionTargetTypes } from '../../interfaces/Misc';
import { Save } from '../../interfaces/Responses';

export interface IApplicationState {
	simulationState: SimulationStates,
	currentLanguage: Languages,
	autoSaveEnabled: boolean,
	autosaveInterval: number,
	isPublishInProgress: boolean,
	settingsSyncStatus: SaveStates,
	isAppInFocus: boolean,
	isViewOnlyMode: boolean,
	propertyClipboard?: IClipboard,
	crashError: string,
	snapSettings: {
		translation: number,
		rotation: number,
		scale: number,
	},
	latestSaveData?: Save,
	inspectionTarget: IInspectionTarget,
	publishSettings?: IPublishSettings,
	simulationAsViewer: boolean,
}

const initialState: IApplicationState = {
	simulationState: SimulationStates.Stopped,
	isViewOnlyMode: false,
	currentLanguage: Languages.EnglishUnitedStates,
	autoSaveEnabled: true,
	autosaveInterval: 10, // in minutes
	isPublishInProgress: false,
	settingsSyncStatus: SaveStates.Pending,
	isAppInFocus: true,
	crashError: "",
	snapSettings: {
		translation: 0.1,
		rotation: 15,
		scale: 0.1,
	},
	inspectionTarget: {
		type: InspectionTargetTypes.None,
		target: null,
	},
	simulationAsViewer: false,
};

const ApplicationSlice = createSlice({
	name: "application",
	initialState,
	reducers: {
		setSimulationState(state, action: PayloadAction<SimulationStates>)
		{
			state.simulationState = action.payload;
		},
		setCurrentLanguage(state, action: PayloadAction<Languages>)
		{
			state.currentLanguage = action.payload;
		},
		setAutoSaveEnabled(state, action: PayloadAction<boolean>)
		{
			state.autoSaveEnabled = action.payload;
		},
		setAutosaveInterval(state, action: PayloadAction<number>)
		{
			state.autosaveInterval = action.payload;
		},
		setPublishInProgress(state, action: PayloadAction<boolean>)
		{
			state.isPublishInProgress = action.payload;
		},
		setApplicationFocus(state, action: PayloadAction<boolean>)
		{
			state.isAppInFocus = action.payload;
		},
		setPropertyClipboard(state, action: PayloadAction<IClipboard>)
		{
			state.propertyClipboard = action.payload;
		},
		setCrashError(state, action: PayloadAction<{ message: string}>)
		{
			const { message } = action.payload;

			state.crashError = message;
		},
		setTranslationSnap(state, action: PayloadAction<number>)
		{
			state.snapSettings.translation = action.payload;
		},
		setRotationSnap(state, action: PayloadAction<number>)
		{
			state.snapSettings.rotation = action.payload;
		},
		setScaleSnap(state, action: PayloadAction<number>)
		{
			state.snapSettings.scale = action.payload;
		},
		setLatestSaveData(state, action: PayloadAction<Save | undefined>)
		{
			state.latestSaveData = action.payload;
		},
		setInspectionTarget(state, action: PayloadAction<IInspectionTarget | undefined>)
		{
			if(!action.payload)
			{
				state.inspectionTarget.type = InspectionTargetTypes.None;
				state.inspectionTarget.target = null;
			}
			else
			{
				state.inspectionTarget = { ...action.payload };
			}
		},
		setPublishSettings(state, action: PayloadAction<IPublishSettings>)
		{
			state.publishSettings = action.payload;
		},
		setIsViewOnlyMode(state, action: PayloadAction<boolean>)
		{
			state.isViewOnlyMode = action.payload;
		},
		setSimulationAsViewer(state, action: PayloadAction<boolean>)
		{
			state.simulationAsViewer = action.payload;
		},
	}
});

export default ApplicationSlice;
